import {
  Component,
  OnInit,
  inject,
  input,
  output,
  ChangeDetectionStrategy,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastService, Toast } from '../services/toast.service';
import { toSignal } from '@angular/core/rxjs-interop';

/**
 * COMPONENTE FIGLIO: Singolo Toast Item
 * Gestisce la propria barra di progresso in modo isolato.
 */
@Component({
  selector: 'app-toast-item',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div 
      [class]="getToastClasses()" 
      class="toast-item pointer-events-auto shadow-xl ring-1 ring-black ring-opacity-5 overflow-hidden relative"
    >
      <div class="flex items-start justify-between p-4 pb-5">
        <div class="flex items-start gap-3">
          <div class="mt-0.5">
            @switch (toast().type) {
              @case ('success') {
                <svg class="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" /></svg>
              }
              @case ('error') {
                <svg class="h-5 w-5 text-red-600" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" /></svg>
              }
              @case ('warning') {
                <svg class="h-5 w-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" /></svg>
              }
              @default {
                <svg class="h-5 w-5 text-primary" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" /></svg>
              }
            }
          </div>
          <p class="text-sm font-semibold leading-relaxed text-gray-800">{{ toast().message }}</p>
        </div>

        @if (toast().dismissible) {
          <button
            (click)="onDismiss.emit(toast().id)"
            type="button"
            class="ml-3 shrink-0 inline-flex text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" /></svg>
          </button>
        }
      </div>

      @if (toast().autoClose && toast().duration) {
        <div class="absolute bottom-0 left-0 h-1 w-full bg-black/5 overflow-hidden">
          <div
            class="h-full origin-left transition-all ease-linear"
            [class]="getBarColor()"
            [style.width.%]="width()"
            [style.transition-duration.ms]="toast().duration"
          ></div>
        </div>
      }
    </div>
  `,
  styles: [`
    .toast-item {
      animation: slideIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
    }
    @keyframes slideIn {
      from { transform: translateX(100%); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }
  `]
})
class ToastItemComponent implements OnInit {
  // NUOVA SINTASSI SIGNAL
  toast = input.required<Toast>();
  onDismiss = output<string>();

  // Signal locale per gestire la larghezza
  width = signal(100);

  ngOnInit() {
    // Avvia la transizione dopo il primo rendering
    setTimeout(() => this.width.set(0), 10);
  }

  getToastClasses() {
    const base = 'rounded-xl min-w-[320px] bg-white/90 backdrop-blur-md border-l-4';
    const borders = {
      success: 'border-green-500',
      error: 'border-red-500',
      warning: 'border-yellow-500',
      info: 'border-primary'
    };
    return `${base} ${borders[this.toast().type] || borders.info}`;
  }

  getBarColor() {
    const colors = {
      success: 'bg-green-500',
      error: 'bg-red-500',
      warning: 'bg-yellow-500',
      info: 'bg-primary'
    };
    return colors[this.toast().type] || colors.info;
  }
}

/**
 * COMPONENTE PADRE: Container
 * Si sottoscrive al servizio e gestisce lo stack dei toast.
 */
@Component({
  selector: 'app-toast-container',
  standalone: true,
  imports: [CommonModule, ToastItemComponent],
  template: `
    <div class="fixed top-4 right-4 z-9999 max-w-md space-y-4 pointer-events-none">
      @for (t of toasts(); track t.id) {
        <app-toast-item 
          [toast]="t" 
          (onDismiss)="handleDismiss($event)"
        />
      }
    </div>
  `
})
export class ToastContainerComponent {
  private toastService = inject(ToastService);

  // Trasforma l'Observable del servizio in un Signal direttamente
  toasts = toSignal(this.toastService.getToasts(), { initialValue: [] as Toast[] });

  handleDismiss(id: string) {
    this.toastService.dismiss(id);
  }
}

export { ToastItemComponent };